import {
    faArrowTurnDownRight,
    faBallotCheck,
    faBuilding,
    faChartBar,
    faCommentsAlt,
    faFolders,
    faHandshake,
    faHouse,
    faHouseFlag,
    faMessageQuestion,
    faRulerCombined,
    faTimes,
    faTools,
    faWallet,
    IconDefinition,
} from '@fortawesome/pro-light-svg-icons';
import { Trans } from '@lingui/react/macro';
import {
    BuyerUnitSelector,
    BygrModule,
    BuyerSurvey,
    ProjectFeatureToggleFlags,
    useProjectHasFeatureToggle,
} from '@ygtechnologies/bygr-frontend-component-library';
import { Menu } from '@ygtechnologies/rubus-ui';
import { CustomLink } from '@/components/navigation';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router';
import { BuyerMeetingDto, hasConfig } from '@/services/booking';
import { getMyUnitProfiles, getProfileImageThumbnailUrl } from '@/services/profiles';
import { getProjectHeaderImageThumbnailUrl } from '@/services/projects';
import { IState } from '@/state/IState';
import { setActiveProfileId } from '@/state/global/creators';
import {
    BuyerChangeRequestStatus,
    BuyerProjectData,
    CommentThreadType,
    EffectiveProjectFeatureConfiguration,
    ProfileUnitInfo,
    ProjectDecimalOption,
    ProjectFeature,
    ProjectFeatureMode,
    TranslatableText,
    UnitOfMeasurement,
} from '@/types';
import { useAppDispatch, useAsyncState } from '@/utils/hooks';
import { getModuleUrl } from '@/utils/returnUrl';
import theme from './BuyerSidebar.module.scss';
import logoImage from '/assets/logo-rucoria-black.png';
import { differenceInDays } from 'date-fns';
import { parseISODate } from '@ygtechnologies/rubus-frontend-utilities';
import { getWhiteLabelLogoUrl } from '@/services/white-label';

export interface BuyerSidebarLink {
    icon: IconDefinition;
    to: string;
    label: TranslatableText;
    featureType: ProjectFeature;
    disabled?: boolean;
    numberIconValue?: number;
    hideNotificationNumber?: boolean;
}

export const BuyerSidebarLinks = [
    { featureType: ProjectFeature.LandingPage, icon: faHouse, to: `/landing`, label: <Trans>Home</Trans> },
    { featureType: ProjectFeature.Dashboard, to: `/dashboard`, icon: faChartBar, label: <Trans>Dashboard</Trans> },
    {
        featureType: ProjectFeature.Options,
        to: `/options`,
        icon: faHouseFlag,
        label: <Trans>Options</Trans>,
        hideNotificationNumber: true,
    },
    { featureType: ProjectFeature.Budget, to: `/budget`, icon: faWallet, label: <Trans>Budget</Trans> },
    {
        featureType: ProjectFeature.ChangeRequests,
        to: `/change-requests`,
        icon: faRulerCombined,
        label: <Trans>Changes</Trans>,
    },
    { featureType: ProjectFeature.Documents, to: `/files`, icon: faFolders, label: <Trans>Documents</Trans> },
    { featureType: ProjectFeature.Chat, to: `/chat`, icon: faCommentsAlt, label: <Trans>Messages</Trans> },
    { featureType: ProjectFeature.Meetings, to: `/book-meeting`, icon: faHandshake, label: <Trans>Meetings</Trans> },
] as BuyerSidebarLink[];

interface BuyerSidebarProps {
    previewFeatures?: EffectiveProjectFeatureConfiguration[];
    previewProjectId?: number;
}

function getMockProjectData(projectId: number): BuyerProjectData {
    return {
        unitType: 'Apartment',
        sellerProfileName: '',
        projectName: '',
        projectId: projectId,
        deadlines: [],
        unitName: '',
        address: '',
        floor: '',
        isInReadOnlyMode: false,
        sellerProfileId: 0,
        features: null,
        changeRequestWarningText: null,
        changeRequestDeadline: null,
        currencyCode: '',
        sellerModules: [],
        buyerModules: [],
        useWhiteLabel: false,
        showBackgroundImageOnMessages: false,
        defaultUnitOfMeasurement: UnitOfMeasurement.Centimeter,
        optionsTerms: '',
        showDefaultTextOnOptions: true,
        useLegacyOptionsView: true,
        useRooms: false,
        decimalOption: ProjectDecimalOption.None,
    };
}

interface BuyerSidebarItemProps {
    children: React.ReactNode;
    to: string;
    icon: IconDefinition;
    badgeNumber?: number;
    dot?: boolean;
    isPreview?: boolean;
    disabled?: boolean;
}

const BuyerSidebarInternalLink: React.FC<BuyerSidebarItemProps> = ({
    isPreview = false,
    children,
    icon,
    badgeNumber,
    dot = false,
    to,
    disabled = false,
}) => {
    const match = useRouteMatch(to);
    return (
        <Menu.Item
            tabIndex={0}
            as={CustomLink}
            to={to}
            icon={icon}
            active={!!match}
            disabled={disabled}
            badgeNumber={badgeNumber}
            dot={dot}
            onClick={(e) => {
                if (isPreview || disabled) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }}>
            {children}
        </Menu.Item>
    );
};

const BuyerSidebar: React.FC<BuyerSidebarProps> = ({ previewFeatures, previewProjectId }) => {
    const user = useSelector((state: IState) => state.global.user);
    const projectData = useSelector((state: IState) => state.buyer.projectData) || getMockProjectData(previewProjectId ?? 0);
    const commentThreads = useSelector((state: IState) => state.buyer.commentThreads);
    const changeRequests = useSelector((state: IState) => state.buyer.changeRequests);
    const residentialUnitId = useSelector((state: IState) => state.buyer.permissions?.residentialUnitId);
    const meetings = useSelector((state: IState) => state.buyer.meetings) || [];
    const features = previewFeatures ?? projectData?.features;
    const isPreview = !!previewFeatures;
    const aftermarketStartUrl = getModuleUrl(BygrModule.Aftermarket, window.APP_CONFIG);
    const showMeetings = useProjectHasFeatureToggle(ProjectFeatureToggleFlags.ShowMeetings);
    const [hasMeetings] = useAsyncState(async () => await hasConfig(projectData.projectId), [projectData.projectId], false);
    const [units, , reloadUnits] = useAsyncState<ProfileUnitInfo[]>(() => getMyUnitProfiles(), [], []);
    const [unitsModalOpen, setIsUnitsModalOpen] = useState(false);
    const sidebarMode = useSelector((state: IState) => state.global.sidebarMode);

    const dispatch = useAppDispatch();
    const shouldShowMeetings = hasMeetings || showMeetings;

    const checkIfMeetingExpired = (meeting: BuyerMeetingDto) => {
        const daysRemaining = differenceInDays(parseISODate(meeting.from), new Date());
        if (daysRemaining <= 0) {
            return false;
        }
        if (!meeting.daysBeforeLockConfirmation) {
            return true;
        }
        return daysRemaining - meeting.daysBeforeLockConfirmation > 0;
    };

    const notificationsByFeature = useMemo<{ [key in ProjectFeature]?: number }>(() => {
        const unsignedDeadlinesCount = projectData.deadlines.filter(
            (deadline) => deadline.hasOptionsAttached && deadline.awaitingSignatures > 0
        ).length;

        return {
            [ProjectFeature.ChangeRequests]: (() => {
                const numberOfMessages = commentThreads
                    .filter((ct) => ct.type === CommentThreadType.ChangeRequest)
                    .reduce((acc, curr) => acc + curr.unreadMessages, 0);

                const numberOfChangeRequests =
                    changeRequests?.filter((cr) => cr.status === BuyerChangeRequestStatus.OfferReceived).length ?? 0;

                return numberOfMessages + numberOfChangeRequests;
            })(),
            [ProjectFeature.Chat]: commentThreads
                .filter((ct) => ct.type === CommentThreadType.ResidentialUnit)
                .reduce((acc, curr) => acc + curr.unreadMessages, 0),
            [ProjectFeature.Options]: unsignedDeadlinesCount,
            [ProjectFeature.Meetings]: meetings.filter(
                (x) => x.confirmed !== undefined && x.confirmed !== null && !x.confirmed && checkIfMeetingExpired(x)
            ).length,
        };
    }, [commentThreads, changeRequests, meetings]);

    if (!features) {
        return isPreview ? (
            <Menu className={theme.preview} mode="sidebar">
                <Menu.Section type="default" />
            </Menu>
        ) : (
            <Menu mode={sidebarMode} mobileClassName={theme.mobileMenu} mobileButtonClassName={theme.mobileButton}>
                <Menu.Section type="default" />
            </Menu>
        );
    }

    const featureMap = features.reduce((result, item, index, array) => {
        result[item.feature] = item.mode;
        return result;
    }, {}) as { [s in keyof typeof ProjectFeature]: ProjectFeatureMode };

    let totalNotificationCount = (Object.keys(notificationsByFeature) as ProjectFeature[]).reduce((result, feature) => {
        const mode = featureMap[feature];

        if (mode === ProjectFeatureMode.Enabled) {
            result += notificationsByFeature[feature] || 0;
        }

        return result;
    }, 0);

    const renderLink = (link: BuyerSidebarLink, index: number) => {
        const mode = featureMap[link.featureType];

        if (mode === ProjectFeatureMode.Hidden) {
            return null;
        }

        if (link.featureType === ProjectFeature.Meetings && !shouldShowMeetings) {
            return null;
        }

        const notificationCount = mode === ProjectFeatureMode.Enabled ? notificationsByFeature[link.featureType] : 0;
        const count = notificationCount ?? 0;
        const showTaskDot = link.hideNotificationNumber && count > 0;
        const badgeNumber = !link.hideNotificationNumber && count > 0 ? count : undefined;

        return (
            <BuyerSidebarInternalLink
                key={link.featureType}
                to={link.to}
                icon={link.icon}
                badgeNumber={badgeNumber}
                dot={showTaskDot}
                disabled={mode === ProjectFeatureMode.Disabled}
                isPreview={isPreview}>
                {link.label}
            </BuyerSidebarInternalLink>
        );
    };

    if (isPreview) {
        return (
            <Menu className={theme.preview} mode="sidebar">
                {BuyerSidebarLinks.map(renderLink)}
            </Menu>
        );
    }

    let projectLogoImage = projectData.useWhiteLabel ? getWhiteLabelLogoUrl(projectData.projectId) : logoImage;
    const addDefaultSrc = (ev) => {
        const url = getProfileImageThumbnailUrl(projectData.sellerProfileId);
        ev.target.src = url;
    };

    const thumbnailUrlGenerator = (projectId) => {
        return getProjectHeaderImageThumbnailUrl(projectId);
    };

    const onSelectProfile = (profile: ProfileUnitInfo) => {
        setActiveProfileId(dispatch, profile.profileId);
        setIsUnitsModalOpen(false);
    };

    return (
        <>
            <Menu mode={sidebarMode} mobileClassName={theme.mobileMenu} mobileButtonClassName={theme.mobileButton}>
                <Menu.Section type="default">
                    <Menu.Item mobileOnly icon={faTimes}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                width: '100%',
                                alignItems: 'center',
                            }}>
                            <Trans>
                                <span>Close</span>
                            </Trans>
                        </div>
                    </Menu.Item>
                    <CustomLink className={theme.logo} to="/">
                        <img src={projectLogoImage} onError={addDefaultSrc} />
                    </CustomLink>

                    <Menu.Item className={theme.noClick} icon={faHouseFlag}>
                        {projectData.unitName + ' ' + projectData.projectName}
                    </Menu.Item>

                    {residentialUnitId && units.length > 1 && (
                        <Menu.Item onClick={() => setIsUnitsModalOpen(true)} icon={faArrowTurnDownRight}>
                            <span className={theme.allUnits}>
                                <Trans>All units</Trans>
                            </span>
                        </Menu.Item>
                    )}

                    <Menu.Spacer />
                    {projectData.buyerModules.length > 1 && (
                        <>
                            <Menu.Item as={CustomLink} to="/" active={true} icon={faBuilding}>
                                <Trans>Home configuration</Trans>
                            </Menu.Item>

                            {projectData.buyerModules.includes(BygrModule.Aftermarket) && (
                                <Menu.Item as="a" href={aftermarketStartUrl} icon={faTools}>
                                    <Trans id="Aftermarket_as_buyer">Aftermarket</Trans>
                                </Menu.Item>
                            )}

                            <Menu.Spacer />
                        </>
                    )}

                    {BuyerSidebarLinks.map(renderLink)}

                    <BuyerSurvey
                        applicationUserId={user?.userId}
                        autoAdvanceOnAnswer={false}
                        inlineNavButtons={true}
                        buttonRenderer={(click) => (
                            <Menu.Item onClick={click} icon={faMessageQuestion} dot>
                                <Trans>Survey</Trans>
                            </Menu.Item>
                        )}
                    />

                    <Menu.Pusher />
                </Menu.Section>
            </Menu>
            {residentialUnitId && (
                <BuyerUnitSelector
                    currentUnitId={residentialUnitId}
                    units={units}
                    onClose={(e) => setIsUnitsModalOpen(e)}
                    isOpen={unitsModalOpen}
                    onSelectProfile={onSelectProfile}
                    thumbnailUrlGenerator={thumbnailUrlGenerator}
                />
            )}
        </>
    );
};

export default BuyerSidebar;
